import React, { useMemo, useState } from 'react'
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { css } from 'glamor'
import ReactModal from 'react-modal'

import * as theme from '../theme'
import {A, HR} from './Typography'
import { Button } from './Button'
import { ColContainer } from './Container'
import {inputSize} from "./Input";
import api from '../lib/api';

export function last4FromStripeSource (stripeSource) {
	try {
		const card = typeof stripeSource === 'string' ? JSON.parse(stripeSource)?.card : stripeSource?.card;
		/* Methods saved via Stripe Link (and other non-card methods) may carry
		 * no card number. Key off a missing last4 rather than brand so a stored
		 * stripeSource without brand: 'link' still avoids rendering
		 * "•••• •••• •••• undefined". */
		if (!card?.last4) return 'Link account'
		return `•••• •••• •••• ${card.last4}`
	} catch (err) {
		return 'Cannot display card number.'
	}
}

export function expiryFromStripeSource (stripeSource) {
	try {
		const card = typeof stripeSource === 'string' ? JSON.parse(stripeSource)?.card : stripeSource?.card;
		/* No card number ⇒ non-card method (e.g. Link); no expiry to show. */
		if (!card?.last4) return '—'
		const mm = String(card.exp_month).padStart(2, '0')
		const yyyy = String(card.exp_year).slice(-2)
		return `${mm} / ${yyyy}`
	} catch (err) {
		return 'Cannot display card expiry.'
	}
}

function errorBorder (error) {
	return css({
		borderColor: error && theme.flavours.danger.foregroundColor,
		borderStyle: error && 'solid',
		borderWidth: error && inputSize.medium.borderWidth,
		borderRadius: error && inputSize.medium.borderRadius,
		padding: error && `10px`,
		marginBottom: error && `16px`,
	})
}

const stripeModalStyles = css({
	display: 'block',
	background: 'white',
	margin: 10,
	padding: 20,
	borderRadius: 6,
	outline: 'none',
	minWidth: 400,
	textAlign: 'center',
}).toString()

const stripeModalOverlayStyles = css({
	backgroundColor: 'rgba(0,0,0,0.2)',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	position: 'fixed',
	zIndex: 100000,
	top: 0,
	right: 0,
	bottom: 0,
	left: 0,
}).toString()

const stripeModalErrorStyles = css({
	background: theme.flavours.danger.backgroundColor,
	border: `1px solid ${theme.flavours.danger.foregroundColor}`,
	borderRadius: 6,
	color: theme.flavours.danger.foregroundColor,
	fontSize: 14,
	fontWeight: 600,
	lineHeight: 1.4,
	marginBottom: 16,
	padding: '10px 12px',
	textAlign: 'left',
}).toString()

export const StripeElementsInput = ({
	value,
	disabled = false,
	appConfig,
	paymentMethod,
	setStripeCustomerId,
	stripeCustomerId,
	currency = 'AUD',
	country,
	installationId,
	guestEmail,
	buttonSize = 'medium',
}) => {
	const [modalOpen, setModalOpen] = useState(false);
	const [error, setError] = useState('');
	/* Pick per-country Stripe publishable key (city.country); fall back to AU. */
	const countryUpper = country ? country.toUpperCase() : null;
	const publishableKey =
		(countryUpper === 'US' && appConfig.STRIPE_PUBLISHABLE_KEY_US) ||
		(countryUpper === 'NZ' && appConfig.STRIPE_PUBLISHABLE_KEY_NZ) ||
		appConfig.STRIPE_PUBLISHABLE_KEY;
	/* Memoize so loadStripe doesn't run on every render — a fresh Promise here
	 * would cause <Elements> to remount and wipe any in-progress card entry
	 * each time modalOpen / error state changes. */
	const stripeInstance = useMemo(() => loadStripe(publishableKey), [publishableKey]);
	const options = { mode: 'setup', currency: currency.toLowerCase() };

	const methodToRender = value || paymentMethod;
	const last4 = methodToRender ? last4FromStripeSource(methodToRender) : '';
	const expiry = methodToRender ? expiryFromStripeSource(methodToRender) : '';
	const hasConfirmedMethod = Boolean(methodToRender || stripeCustomerId);

	if (disabled) {
		return (
			<div>
				<ColContainer columnTemplate="1fr 1fr 1fr">
					<div>Card number</div>
					<div>{last4}</div>
				</ColContainer>
				<HR spaceAbove="small" spaceBelow="small" />
				<ColContainer columnTemplate="1fr 1fr 1fr">
					<div>Card expiry</div>
					<div>{expiry}</div>
				</ColContainer>
				<HR spaceAbove="small" spaceBelow="small" />
			</div>
		)
	}

	return (
		<div>
			<ColContainer columnTemplate="auto 1fr">
				<Button size={buttonSize} onClick={() => setModalOpen(true)}>
					{hasConfirmedMethod ? 'Update card' : '+ Add Card'}
				</Button>
				<div className={css(errorBorder(error), { textAlign: 'right' })}>
					<span className={css({
						color: error
							? theme.flavours.danger.foregroundColor
							: undefined,
					})}>
						{hasConfirmedMethod
							? methodToRender
								? `${last4} · ${expiry}`
								: 'Payment method added'
							: 'Add customer card details to authorise parking fee'}
					</span>
				</div>
			</ColContainer>

			<Elements stripe={stripeInstance} options={options}>
				<StripeModal
					modalOpen={modalOpen}
					setModalOpen={setModalOpen}
					error={error}
					setError={setError}
					setStripeCustomerId={setStripeCustomerId}
					installationId={installationId}
					guestEmail={guestEmail}
					buttonSize={buttonSize}
				/>
			</Elements>
		</div>
	)
}

const StripeModal = ({ modalOpen, setModalOpen, error, setError, setStripeCustomerId, installationId, guestEmail, buttonSize }) => {
	const stripe = useStripe();
	const elements = useElements();
	const [loading, setLoading] = useState(false);

	const handleSubmit = async (e) => {
		e.preventDefault();

		if (!stripe || !elements || loading) return;

		setLoading(true);
		const { error: submitError } = await elements.submit();
		if (submitError) {
			setError('Please enter valid card details');
			setLoading(false);
			return null;
		}

		let data;
		try {
			({ data } = await api.createIntent(installationId, guestEmail));
		} catch (err) {
			/* createIntent rejected (network/server error) — without this catch
			 * the modal stays stuck on the spinner and the rejection bubbles up
			 * as an unhandled promise error. Reset loading and surface the
			 * failure to the operator. */
			setLoading(false);
			setError('Unable to initialise payment setup. Please try again.');
			return;
		}
		const { client_secret: clientSecret, customer_id: stripeCustomerId } = data;

		const { error } = await stripe.confirmSetup({
			elements,
			clientSecret,
			redirect: 'if_required',
			confirmParams: {
				return_url: window.location.origin,
			},
		});

		if (error) {
			setLoading(false);
			setError('Invalid payment method. Please enter valid card details');
			return;
		}

		setLoading(false);
		setError('');
		setStripeCustomerId(stripeCustomerId);

		setModalOpen(false);
	};

	return (
		<ReactModal
			isOpen={modalOpen}
			onRequestClose={() => setModalOpen(false)}
			className={stripeModalStyles}
			overlayClassName={stripeModalOverlayStyles}
		>
			{error ? <div className={stripeModalErrorStyles}>{error}</div> : null}
			<PaymentElement />
			<br/>
					<Button
						size={buttonSize}
						type="button"
						onClick={() => setModalOpen(false)}
					>
				Cancel
			</Button>
					<Button
						size={buttonSize}
						type="submit"
				disabled={loading}
				style={{ marginLeft: '1rem' }}
				onClick={(e) => handleSubmit(e)}
			>
				Add Details
			</Button>
		</ReactModal>
	);
};
